import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Tooltip, Skeleton, alpha, keyframes } from '@mui/material';
import { m } from 'framer-motion';
import Iconify from '../Iconify';
import { SvgColor } from '../svg-color';
import TrendPill from './TrendPill';
import { FADED_OPACITY } from './totalChip';
import {
    CARD_LABEL,
    CARD_NUMBER,
    CHIP,
    TILE_NUMBER,
    CARD_DOT_SIZE,
    CARD_LABEL_GAP,
    HEADER_NUMBER_GAP,
    NUMBER_CHIPS_GAP,
    CHIP_GAP,
    CHIP_DOT_SIZE,
    CHIP_DOT_GAP,
} from './cardTypography';
import {
    URGENCY_COLORS,
    URGENCY_STATES,
    UNSELECTED_BORDER_COLOR,
    CHIP_RED,
    CHIP_GREEN,
    SELECTED_NEUTRAL_COLOR,
    FOCUS_RING_COLOR,
} from './actionCardColors';

// ── Count-up hook: animates a number from 0 → target ──
function useCountUp(target, duration = 800, enabled = true) {
    const [current, setCurrent] = useState(enabled ? 0 : target);
    const rafRef = useRef(null);
    const startTimeRef = useRef(null);

    useEffect(() => {
        if (!enabled) { setCurrent(target || 0); return undefined; }
        if (typeof target !== 'number' || Number.isNaN(target) || target === 0) {
            setCurrent(target || 0);
            return undefined;
        }
        startTimeRef.current = null;
        if (rafRef.current) cancelAnimationFrame(rafRef.current);

        const animate = (timestamp) => {
            if (!startTimeRef.current) startTimeRef.current = timestamp;
            const elapsed = timestamp - startTimeRef.current;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - (1 - progress) ** 3; // ease-out cubic
            setCurrent(Math.round(target * eased));
            if (progress < 1) rafRef.current = requestAnimationFrame(animate);
        };
        rafRef.current = requestAnimationFrame(animate);
        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target, duration, enabled]);

    return current;
}

// ── Framer-motion variants for staggered card entrance ──
const containerVariants = {
    hidden: {},
    visible: {
        transition: { staggerChildren: 0.08, delayChildren: 0.3 },
    },
};

const cardMotionVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.97 },
    visible: {
        opacity: 1, y: 0, scale: 1,
        transition: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] },
    },
};

// Helpful tooltips for each widget type
const WIDGET_TOOLTIPS = {
    '1': 'New leads waiting to be contacted. Reach out quickly to maximize conversion.',
    '2': 'Follow up with new patients who haven\'t booked a consultation yet.',
    '3': 'Patients with upcoming consultations. Ensure they\'re prepared and confirmed.',
    '4': 'Patients who attended consultation but haven\'t started treatment. Follow up to close the sale.',
    '5': 'ClinChecks ready to be sent to patients for approval.',
    '6': 'Patients who missed their consultation. Re-engage them to reschedule.',
    '7': 'WhatsApp messages needing a response. Quick replies improve patient satisfaction.',
    'BOOKED_AWAITING': 'Consultations that have passed but attendance hasn\'t been recorded. Record outcome to keep data accurate.',
};

// Priority order for sorting
const PRIORITY_ORDER = {
    [URGENCY_STATES.URGENT]: 1,
    [URGENCY_STATES.WARNING]: 2,
    [URGENCY_STATES.GOOD]: 3,
    [URGENCY_STATES.NEUTRAL]: 4,
};

// Animation keyframes
const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const shimmer = keyframes`
    0% {
        background-position: -200px 0;
    }
    100% {
        background-position: calc(200px + 100%) 0;
    }
`;

// Subtle "live / date-filtered" breathing glow. Applied to cards whose metric is scoped
// to the active date range (card.rangeActive), so it's visually obvious which numbers
// reflect the picker. Slow + low-opacity green (matches the GOOD accent) so it reads as
// slick, not noisy.
const rangeGlow = keyframes`
    0%, 100% {
        box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.35), 0 0 10px 0 rgba(16, 185, 129, 0.14);
    }
    50% {
        box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.65), 0 0 16px 2px rgba(16, 185, 129, 0.30);
    }
`;

// Styles matching the mockup
const cardWrapperStyle = {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    // Cards in one row stretch to the tallest card's height (content stays top-aligned).
    alignItems: 'stretch',
    width: '100%',
    mb: 3,
};

const SELECTED_CARD_SHADOW = '0 8px 24px rgba(15,23,42,0.12)';

const getCardStyle = (urgencyState, isSelected, isLoaded, isFirstLoad, nonInteractive = false, compact = false) => {
    const baseStyle = {
        flex: '1 1 0',
        minWidth: '140px',
        // Task R: no min-height — a card is exactly as tall as its content; the row's
        // align-items: stretch equalises heights.
        padding: compact ? '10px 12px' : '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        borderRadius: '12px',
        border: `2px solid ${UNSELECTED_BORDER_COLOR}`,
        '@media (max-width: 576px)': {
            flex: '1 1 calc(50% - 6px)',
            minWidth: 'unset',
        },
        background: '#fff',
        // Non-interactive cards (display-only metrics with no click handler) must not
        // advertise interactivity: default cursor + no hover-lift. Interactive cards keep
        // pointer + lift (and a focus-visible outline for keyboard users).
        cursor: nonInteractive ? 'default' : 'pointer',
        transition: 'border-color 0.3s ease, background 0.3s ease, box-shadow 0.2s ease, transform 0.2s ease',
        position: 'relative',
        overflow: 'hidden',
        // Skip CSS animation when framer-motion handles the entrance
        animation: (!isFirstLoad && isLoaded) ? `${fadeInUp} 0.35s ease-out` : 'none',
        ...(nonInteractive ? {} : {
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            },
            '&:focus-visible': {
                outline: `2px solid ${FOCUS_RING_COLOR}`,
                outlineOffset: '2px',
            },
        }),
    };

    // Single selection rule for every strip/board: NO dark border. Selected = soft
    // elevated shadow + lift (the stage-accent top bar / urgency bar is rendered
    // separately and stays). Unselected = resting border; hover = light lift.
    const selectedStyle = isSelected ? {
        boxShadow: SELECTED_CARD_SHADOW,
        transform: 'translateY(-2px)',
        ...(nonInteractive ? {} : { '&:hover': { transform: 'translateY(-2px)', boxShadow: SELECTED_CARD_SHADOW } }),
    } : {};

    return { ...baseStyle, ...selectedStyle };
};

// Filter cards (a card row whose cards filter the list below, e.g. Call Logs): the active
// card must read as ON at a glance, unlike hover. Primary tint + primary border + a check
// mark in place of its dot (the primary is the app's "selected" colour, e.g. the date
// presets); the card's own accent bar stays. Hover stays the light lift.
const getFilterCardStyle = (isSelected) => {
    if (!isSelected) return {};
    const ring = (theme) => `0 0 0 1px ${theme.palette.primary.main}, ${SELECTED_CARD_SHADOW}`;
    return {
        borderColor: 'primary.main',
        background: (theme) => alpha(theme.palette.primary.main, 0.08),
        boxShadow: ring,
        '&:hover': { transform: 'translateY(-2px)', boxShadow: ring },
    };
};

// Left colored accent bar style
const getLeftBarStyle = (urgencyState) => {
    const colors = {
        [URGENCY_STATES.URGENT]: CHIP_RED.text,
        [URGENCY_STATES.WARNING]: '#f59e0b',
        [URGENCY_STATES.GOOD]: CHIP_GREEN.text,
        [URGENCY_STATES.NEUTRAL]: 'transparent',
    };

    return {
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        width: '4px',
        background: colors[urgencyState] || 'transparent',
        borderRadius: '10px 0 0 10px',
    };
};

// Task O — stage-accent top border, replacing the urgency-driven left bar on cards that
// set `card.accentColor` (the "due" cards — see actionCardColors.js's STAGE_ACCENT_COLORS
// doc comment for why). Mirrors LaunchpadPipeline's StageCard `&::before` top bar so these
// cards visually match "Your pipeline" above them.
const getTopBarStyle = (accentColor) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: accentColor,
    borderRadius: '10px 10px 0 0',
});

const cardHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    mb: NUMBER_CHIPS_GAP, // number → chips
};

// Unified text colors per urgency (icon, number, label all match)
const URGENCY_TEXT_COLORS = {
    [URGENCY_STATES.URGENT]: CHIP_RED.text,
    [URGENCY_STATES.WARNING]: '#b45309',
    [URGENCY_STATES.GOOD]: CHIP_GREEN.text,
    [URGENCY_STATES.NEUTRAL]: '#475569',
};

const URGENCY_ICON_BG = {
    [URGENCY_STATES.URGENT]: '#fef2f2',
    [URGENCY_STATES.WARNING]: '#fffbeb',
    [URGENCY_STATES.GOOD]: '#ecfdf5',
    [URGENCY_STATES.NEUTRAL]: '#f1f5f9',
};

const cardNumberStyle = CARD_NUMBER;

const getIconWrapperStyle = (bgColor) => ({
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: bgColor || '#f1f5f9',
    flexShrink: 0,
});

// Header copied from LaunchpadPipeline StageCard (8px dot, 7px gap, 11px/800 upper-case
// text.secondary label) so every widget card matches "Your pipeline".
const cardTopRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    mb: HEADER_NUMBER_GAP, // header → number
};

const cardLabelGroupStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: CARD_LABEL_GAP,
    minWidth: 0,
    maxWidth: '100%',
};

const cardDotStyle = {
    width: CARD_DOT_SIZE,
    height: CARD_DOT_SIZE,
    borderRadius: '999px',
    flexShrink: 0,
};

// Shared header typography lives in ./cardTypography (dependency-free) — imported above.

const timeContextStyle = {
    display: 'flex',
    gap: CHIP_GAP,
    flexWrap: 'wrap',
    alignItems: 'center',
    minHeight: '22px',
};

const getTimeBadgeStyle = (type) => {
    const baseStyle = {
        ...CHIP,
        display: 'inline-flex',
        alignItems: 'center',
        gap: CHIP_DOT_GAP,
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    };

    const typeStyles = {
        overdue: {
            background: CHIP_RED.bg,
            color: CHIP_RED.text,
        },
        'due-today': {
            background: '#fef3c7',
            color: '#d97706',
        },
        'on-track': {
            background: CHIP_GREEN.bg,
            color: CHIP_GREEN.text,
        },
        info: {
            background: '#f1f5f9',
            color: '#475569',
        },
    };

    return { ...baseStyle, ...typeStyles[type] };
};

const priorityDotStyle = (color) => ({
    width: `${CHIP_DOT_SIZE}px`,
    height: `${CHIP_DOT_SIZE}px`,
    borderRadius: '50%',
    display: 'inline-block',
    bgcolor: color,
    flexShrink: 0,
});

// Task O addendum — ONE ordering rule for every isV2Split chip row, so green/amber/red
// ordering can't drift per-card: green ('on-track') always LEFT, red ('overdue') always
// RIGHT, amber ('due-today') in between. Applied as a stable sort on whatever chips a
// card supplies (via splitChips, or the two legacy overdue/dueToday props folded into
// the same shape below) — callers never hand-order chips themselves.
const CHIP_TONE_ORDER = { 'on-track': 0, info: 1, 'due-today': 2, overdue: 3 };
// A chip flagged `pinRight` (e.g. the grey "N new leads" total) always sorts LAST and is
// right-aligned by the caller's sx, regardless of tone.
const PIN_RIGHT_ORDER = 1000;
const sortChipsByTone = (chips) => (
    chips
        .map((chip, index) => ({ chip, index }))
        .sort((a, b) => {
            const orderOf = (c) => (c.pinRight ? PIN_RIGHT_ORDER : (CHIP_TONE_ORDER[c.type] ?? 99));
            const diff = orderOf(a.chip) - orderOf(b.chip);
            // Stable sort: ties (e.g. two 'info' badges) keep their original relative order.
            return diff !== 0 ? diff : a.index - b.index;
        })
        .map(({ chip }) => chip)
);

// Skeleton card styles
const skeletonCardStyle = {
    flex: '1 1 0',
    minWidth: '140px',
    minHeight: '92px',
    padding: '14px 16px',
    borderRadius: '12px',
    border: '2px solid #f1f5f9',
    background: '#fafbfc',
    position: 'relative',
    overflow: 'hidden',
    '@media (max-width: 576px)': {
        flex: '1 1 calc(50% - 6px)',
        minWidth: 'unset',
    },
};

const skeletonShimmerOverlay = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)`,
    backgroundSize: '200px 100%',
    backgroundRepeat: 'no-repeat',
    animation: `${shimmer} 1.5s ease-in-out infinite`,
};

// Skeleton card component
const ActionCardSkeleton = () => (
    <Box sx={skeletonCardStyle}>
        <Box sx={skeletonShimmerOverlay} />
        <Box sx={cardHeaderStyle}>
            <Skeleton variant="text" width={48} height={32} sx={{ borderRadius: '6px' }} />
            <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: '8px' }} />
        </Box>
        <Skeleton variant="text" width="75%" height={16} sx={{ mb: '10px', borderRadius: '4px' }} />
        <Skeleton variant="rounded" width={80} height={20} sx={{ borderRadius: '10px' }} />
    </Box>
);

// Hover handler for elements that carry their OWN tooltip: stops the mouse-over bubbling to the
// card-level Tooltip so two tooltips never stack.
const STOP_HOVER_PROPS = { onMouseOver: (e) => e.stopPropagation() };

// TimeBadge Component
// An optional onClick turns the chip into an interactive button (e.g. a "+N more"
// affordance). Clicking stops propagation so it doesn't also trigger the card's onClick.
const TimeBadge = ({ type, text, showDot = false, dotColor = CHIP_RED.text, onClick = null, selected = false, sx = null, testId = undefined, tooltip = undefined }) => {
    const interactive = typeof onClick === 'function';
    // Task O (chip polish): no dotted underline — the chip is just the tinted pill +
    // dot + text. Interactivity reads from cursor/hover/focus-visible alone (and the
    // tooltip, where present, is a separate affordance — see ActionCard's own
    // Tooltip wrapper, unaffected by removing this decoration).
    const interactiveSx = interactive
        ? {
            cursor: 'pointer',
            userSelect: 'none',
            textDecoration: 'none',
            '&:hover': { filter: 'brightness(0.95)' },
            '&:focus-visible': { outline: `2px solid ${FOCUS_RING_COLOR}`, outlineOffset: '2px' },
        }
        : {};
    // Selected state reuses the chip's own type colour (already set as `color` by
    // getTimeBadgeStyle) via `currentColor` — no new colours introduced. A ring +
    // bolder weight is enough to read as "active filter" at this chip's 10px size.
    const selectedSx = selected
        ? {
            boxShadow: 'inset 0 0 0 1.5px currentColor',
            fontWeight: 700,
        }
        : {};
    const badge = (
        <Box
            component="span"
            data-testid={testId}
            // A chip with its own tooltip must not also open the card-level tooltip behind it.
            {...(tooltip ? STOP_HOVER_PROPS : {})}
            sx={{ ...getTimeBadgeStyle(type), ...interactiveSx, ...selectedSx, ...(sx || {}) }}
            {...(interactive
                ? {
                    role: 'button',
                    tabIndex: 0,
                    'aria-pressed': selected,
                    onClick: (e) => { e.stopPropagation(); onClick(e); },
                    onKeyDown: (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            onClick(e);
                        }
                    },
                }
                : {})}
        >
            {showDot && <Box component="span" sx={priorityDotStyle(dotColor)} />}
            {text}
        </Box>
    );
    return tooltip ? <Tooltip title={tooltip} arrow placement="top" enterDelay={400}>{badge}</Tooltip> : badge;
};

TimeBadge.propTypes = {
    type: PropTypes.oneOf(['overdue', 'due-today', 'on-track', 'info']).isRequired,
    text: PropTypes.string.isRequired,
    showDot: PropTypes.bool,
    dotColor: PropTypes.string,
    onClick: PropTypes.func,
    selected: PropTypes.bool,
    sx: PropTypes.object,
    testId: PropTypes.string,
    tooltip: PropTypes.string,
};


// ActionCard Component
const ActionCard = ({ card, onClick = null, isSelected = false, isLoaded = false, isFirstLoad = false }) => {
    const { count, displayCount, label, icon, iconBgColor, urgencyState, key, tooltip } = card;
    const ownBadges = card.badges || [];
    // A card is interactive when (a) the consumer wired an onClick AND (b) it isn't
    // explicitly flagged non-interactive (display-only metrics like Total Value / Go-Aheads).
    // Interactive cards get pointer + hover-lift + keyboard (role/tabIndex/Enter+Space);
    // non-interactive cards look and behave as static.
    const isInteractive = typeof onClick === 'function' && !card.nonInteractive && !card.disabled;
    const handleActivate = () => { if (isInteractive) onClick(card); };

    // ISS-036 / ISS-037 — a selected card reading 0 sits above an empty table, which reads
    // as "broken" rather than "filtered". Name the way out, on the card that caused it.
    // Central here rather than per widget so every board that pre-selects a card gets the
    // same affordance: Booked (Upcoming this week), New (Due Today), Attended (Due Now),
    // Started (Due Now / In Progress / …).
    //
    // Gated on selected AND empty AND interactive: a selected card with rows explains
    // itself, an unselected card is filtering nothing, and a display-only metric is never
    // selected. `clearFilterHint: false` opts a card out; a card supplying its own
    // 'clear-filter' badge keeps it rather than getting a duplicate.
    const wantsClearHint = isSelected
        && isInteractive
        && card.clearFilterHint !== false
        && (typeof count === 'number' ? count === 0 : displayCount === 0);
    const badges = (wantsClearHint && !ownBadges.some((b) => b?.key === 'clear-filter'))
        ? [...ownBadges, {
            key: 'clear-filter',
            type: 'info',
            // Copy rule: verb + outcome, and never "tap" — this is a desktop CRM.
            text: 'Clear to see all',
            onClick: () => onClick(card),
        }]
        : ownBadges;
    // Tasks-v2 "New patient follow ups": show Overdue + Due Today as two
    // separate, labelled numbers (mirrors the V2 board's two columns) instead
    // of one combined total.
    const isV2Split = !!card.isV2Split;
    const overdue = card.overdue || 0;
    const dueToday = card.dueToday || 0;
    // Use tooltip from card data, fall back to internal WIDGET_TOOLTIPS
    const tooltipText = tooltip || WIDGET_TOOLTIPS[key] || '';
    // Use displayCount if provided (e.g., "20+"), otherwise use count
    const shownCount = displayCount !== undefined ? displayCount : count;

    // Animate numbers on first load (count up from 0); skip for string counts like "20+"
    const numericCount = typeof shownCount === 'number' ? shownCount : 0;
    const animatedCount = useCountUp(numericCount, 800, isFirstLoad);
    const displayedCount = isFirstLoad && typeof shownCount === 'number' ? animatedCount : shownCount;

    // Task O — stage accent: a card with `accentColor` set (the "due" cards) always
    // reads as plain dark text regardless of urgencyState (the user's "too much red"
    // feedback was this exact headline/left-bar coupling to urgency) and tints its icon
    // background + icon glyph with its stage colour instead of the urgency palette.
    // See actionCardColors.js's STAGE_ACCENT_COLORS for where these hex values come from.
    const { accentColor } = card;
    const textColor = accentColor ? 'text.primary' : (URGENCY_TEXT_COLORS[urgencyState] || '#475569');
    const iconBg = accentColor ? `${accentColor}1A` : (URGENCY_ICON_BG[urgencyState] || iconBgColor || '#f1f5f9');
    const iconColor = accentColor || textColor;
    // Header dot: stage accent, else the urgency tone, else muted (neutral cards).
    const dotColor = accentColor || (urgencyState && urgencyState !== URGENCY_STATES.NEUTRAL ? textColor : 'text.disabled');

    // Cards whose metric is scoped to the active date range get a subtle breathing glow so
    // it's visually clear which numbers reflect the picker. A steady green border accent
    // (when not already selected) anchors the glow. Applied last so it layers over the base.
    const rangeActiveSx = card.rangeActive
        ? {
            ...(isSelected ? {} : { borderColor: 'rgba(16, 185, 129, 0.55)' }),
            animation: `${rangeGlow} 2.4s ease-in-out infinite`,
        }
        : {};
    const rangeActiveBlockedSx = card.rangeActiveBlocked
        ? {
            opacity: 0.72,
            borderColor: 'rgba(148, 163, 184, 0.45)',
            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
        }
        : {};

    // Deactivated cards (e.g. "Due Now" while a historical date range is active — a
    // real-time task metric that doesn't combine with a date filter): dim + non-interactive
    // so it's clearly not applicable to the current selection.
    const disabledSx = card.disabled
        ? { opacity: 0.4, filter: 'grayscale(0.5)', pointerEvents: 'none' }
        : {};

    // Shared grey total chip (see ./totalChip) — always last in the row, pinned right by its own sx.
    const totalChipEl = card.totalChip ? (
        <TimeBadge
            key="__total"
            type={card.totalChip.type}
            text={card.totalChip.text}
            showDot={false}
            onClick={card.totalChip.onClick}
            selected={!!card.totalChip.selected}
            sx={card.totalChip.sx}
            testId={card.totalChip.testId}
            tooltip={card.totalChip.tooltip}
        />
    ) : null;

    const cardContent = (
            <Box
            sx={{ ...getCardStyle(urgencyState, isSelected, isLoaded, isFirstLoad, !isInteractive, card.size === 'sm'), ...rangeActiveSx, ...rangeActiveBlockedSx, ...disabledSx, ...(card.filterCard ? { px: '10px', ...getFilterCardStyle(isSelected) } : {}) }}
            onClick={handleActivate}
            {...(isInteractive
                ? {
                    role: 'button',
                    tabIndex: 0,
                    'aria-pressed': isSelected,
                    onKeyDown: (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleActivate();
                        }
                    },
                }
                : {})}
        >
            {/* Left accent bar (urgency) — or, for "due" cards with a stage accentColor,
                a thin top border instead (Task O — see textColor/iconBg comment above). */}
            {accentColor ? (
                <Box sx={getTopBarStyle(accentColor)} />
            ) : (
                urgencyState !== URGENCY_STATES.NEUTRAL && <Box sx={getLeftBarStyle(urgencyState)} />
            )}

            {/* Task Q — pipeline-style header (LaunchpadPipeline StageCard): stage dot +
                UPPER-CASE label above the big number; icon stays top-right. */}
            <Box data-testid="action-card-header" sx={cardTopRowStyle}>
                <Box sx={cardLabelGroupStyle}>
                    {card.filterCard && isSelected ? (
                        <Iconify icon="eva:checkmark-circle-2-fill" width={14} data-testid="action-card-check" sx={{ color: 'primary.main', flexShrink: 0 }} />
                    ) : (
                        <Box
                            component="span"
                            data-testid="action-card-dot"
                            sx={{ ...cardDotStyle, bgcolor: dotColor }}
                        />
                    )}
                    <Typography sx={card.filterCard ? { ...CARD_LABEL, letterSpacing: '0.2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } : CARD_LABEL}>{label}</Typography>
                </Box>
                {icon && (
                    <Box sx={getIconWrapperStyle(iconBg)}>
                        {typeof icon === 'string' && icon.startsWith('svg:') ? (
                            <SvgColor
                                src={`/assets/icons/navbar/${icon.slice(4)}.svg`}
                                sx={{ width: 16, height: 16, color: iconColor }}
                            />
                        ) : (
                            <Iconify icon={icon} width={16} height={16} sx={{ color: iconColor }} />
                        )}
                    </Box>
                )}
            </Box>
            <Box sx={cardHeaderStyle}>
                {/* Split cards keep the SAME big-number header as the standard cards
                    (combined Overdue+Due-Today total) so the card's vertical rhythm
                    lines up with the rest of the row. The Overdue / Due-Today breakdown
                    is shown as two chips in the badge row below, matching the standard
                    card's chip treatment. */}
                {(() => {
                    const numberEl = <Typography sx={{ ...cardNumberStyle, ...(card.size === 'sm' ? TILE_NUMBER : {}), ...(card.valueSx || {}), ...(card.fadeEmphasis ? { opacity: FADED_OPACITY } : {}), color: card.valueSx?.color || textColor }}>{displayedCount}</Typography>;
                    return card.headlineTooltip
                        ? <Tooltip title={card.headlineTooltip} arrow placement="top" enterDelay={400}><span {...STOP_HOVER_PROPS}>{numberEl}</span></Tooltip>
                        : numberEl;
                })()}
            </Box>
            {isV2Split ? (
                // Two chips replacing the single status badge — same chip style and
                // spacing (timeContextStyle + getTimeBadgeStyle) as the standard cards.
                // `splitChips` (generic, arbitrary bucket pairs — e.g. the New-lead
                // table's "No active task" card: Checklist complete / No tasks) takes
                // priority when supplied; otherwise falls back to the original
                // hardcoded Overdue/Due Today chips (Attended tab's "Due Now" card),
                // unchanged, so that caller needs no changes. Task O: RED is reserved
                // for "Overdue" — "Due Today" is a GREEN on-track chip, reusing the
                // same tokens the "Checklist complete" / "N this week" chips use.
                // Task O addendum: whichever shape supplied the chips, they're always
                // rendered through sortChipsByTone — green left, red right, no per-card
                // ordering to drift.
                <Box sx={timeContextStyle}>
                    {sortChipsByTone(
                        Array.isArray(card.splitChips) && card.splitChips.length > 0
                            ? card.splitChips
                            : [
                                {
                                    bucket: 'overdue',
                                    text: `${overdue} Overdue`,
                                    type: 'overdue',
                                    dotColor: CHIP_RED.text,
                                    onClick: card.onOverdueClick,
                                    selected: card.activeBucket === 'overdue',
                                },
                                {
                                    bucket: 'due_today',
                                    text: `${dueToday} Due Today`,
                                    type: 'on-track',
                                    dotColor: CHIP_GREEN.text,
                                    onClick: card.onDueTodayClick,
                                    selected: card.activeBucket === 'due_today',
                                },
                            ]
                    ).map((chip, chipIdx) => (
                        <TimeBadge
                            key={chip.bucket || `${chip.type}-${chip.text}-${chipIdx}`}
                            type={chip.type}
                            text={chip.text}
                            showDot={chip.showDot !== false}
                            dotColor={chip.dotColor}
                            onClick={chip.onClick}
                            selected={!!chip.selected}
                            sx={card.fadeEmphasis ? { ...(chip.sx || {}), opacity: FADED_OPACITY } : chip.sx}
                            testId={chip.testId}
                            tooltip={chip.tooltip}
                        />
                    ))}
                    {totalChipEl}
                    {card.delta && <TrendPill value={card.delta.value} inverted={!!card.delta.inverted} tooltip={card.delta.tooltip} />}
                </Box>
            ) : (
                (badges.length > 0 || !!card.delta || !!card.totalChip) && (
                    <Box sx={timeContextStyle}>
                        {badges.map((badge, badgeIdx) => (
                            <TimeBadge
                                // Prefer an explicit per-badge key; many badges can share the
                                // same `type` (e.g. a per-treatment breakdown of N 'info' chips),
                                // so keying on type alone collides. Fall back to type+text+index.
                                key={badge.key || `${badge.type}-${badge.text}-${badgeIdx}`}
                                type={badge.type}
                                text={badge.text}
                                showDot={badge.showDot}
                                dotColor={badge.dotColor}
                                onClick={badge.onClick}
                                sx={card.fadeEmphasis ? { opacity: FADED_OPACITY } : undefined}
                            />
                        ))}
                        {totalChipEl}
                        {card.delta && <TrendPill value={card.delta.value} inverted={!!card.delta.inverted} tooltip={card.delta.tooltip} />}
                    </Box>
                )
            )}
        </Box>
    );

    if (tooltipText) {
        return (
            <Tooltip
                title={tooltipText}
                arrow
                placement="top"
                enterDelay={500}
                slotProps={{
                    tooltip: {
                        sx: {
                            bgcolor: '#1e293b',
                            fontSize: '12px',
                            maxWidth: 220,
                            textAlign: 'center',
                            '& .MuiTooltip-arrow': {
                                color: '#1e293b',
                            },
                        },
                    },
                }}
            >
                {cardContent}
            </Tooltip>
        );
    }

    return cardContent;
};

ActionCard.propTypes = {
    card: PropTypes.shape({
        key: PropTypes.string.isRequired,
        count: PropTypes.number.isRequired,
        displayCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.node]),
        label: PropTypes.string.isRequired,
        icon: PropTypes.string,
        iconBgColor: PropTypes.string,
        urgencyState: PropTypes.oneOf(Object.values(URGENCY_STATES)),
        // Opt a card OUT of the automatic "Clear to see all" hint (ISS-036/037).
        clearFilterHint: PropTypes.bool,
        badges: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.string,
                type: PropTypes.oneOf(['overdue', 'due-today', 'on-track', 'info']),
                text: PropTypes.string,
                showDot: PropTypes.bool,
                dotColor: PropTypes.string,
                onClick: PropTypes.func,
            })
        ),
        tooltip: PropTypes.string,
        isV2Split: PropTypes.bool,
        overdue: PropTypes.number,
        dueToday: PropTypes.number,
        // isV2Split only: click handlers for the two split chips, and which bucket
        // (if any) is the currently active filter — renders that chip's selected state.
        onOverdueClick: PropTypes.func,
        onDueTodayClick: PropTypes.func,
        activeBucket: PropTypes.oneOf(['overdue', 'due_today', null]),
        // isV2Split, generic alternative to the overdue/dueToday pair above — an
        // arbitrary pair (or more) of bucket chips, e.g. the New-lead table's
        // "No active task" card (Checklist complete / No tasks). Takes priority
        // over overdue/dueToday when present.
        splitChips: PropTypes.arrayOf(
            PropTypes.shape({
                bucket: PropTypes.string,
                text: PropTypes.string.isRequired,
                type: PropTypes.oneOf(['overdue', 'due-today', 'on-track', 'info']).isRequired,
                showDot: PropTypes.bool,
                dotColor: PropTypes.string,
                onClick: PropTypes.func,
                selected: PropTypes.bool,
            })
        ),
        // Display-only metric (no click action) — renders static: default cursor, no
        // hover-lift, no button role / keyboard handler.
        nonInteractive: PropTypes.bool,
        // Metric is scoped to the active date range → subtle breathing glow (see rangeGlow).
        rangeActive: PropTypes.bool,
        // Real-time metric currently outside the active date-range context. Still clickable,
        // but visually quieter than the range-scoped cards.
        rangeActiveBlocked: PropTypes.bool,
        // Not applicable to the current selection (e.g. Due Now while a date range is active)
        // → dimmed + non-interactive.
        disabled: PropTypes.bool,
        // Task O: a fixed per-stage accent (hex, see actionCardColors.js's
        // STAGE_ACCENT_COLORS) for the "due" cards — overrides urgencyState's usual
        // headline/left-bar/icon colouring with plain dark text + a thin top border +
        // a stage-tinted icon, so a backlog of overdue items doesn't paint the whole
        // card red/amber.
        accentColor: PropTypes.string,
        // Compact variant for dense grids (StatCard size='sm').
        size: PropTypes.oneOf(['md', 'sm']),
        // A filter card (Call Logs): strong active state (tint + accent border + check)
        // and a one-line label.
        filterCard: PropTypes.bool,
        // Period-over-period change → TrendPill in the chips row ({ value, inverted }).
        delta: PropTypes.shape({ value: PropTypes.number, inverted: PropTypes.bool, tooltip: PropTypes.string }),
        // Extra sx merged onto the big number (e.g. ellipsis for text values).
        valueSx: PropTypes.object,
        // Shared grey total chip (buildTotalChip) pinned right in the chips row; `fadeEmphasis`
        // drops the headline + tone chips to 0.45 while that chip is the active selection.
        totalChip: PropTypes.object,
        fadeEmphasis: PropTypes.bool,
        // Tooltip on the big number only (the card-level `tooltip` covers the whole card).
        headlineTooltip: PropTypes.string,
    }).isRequired,
    onClick: PropTypes.func,
    isSelected: PropTypes.bool,
    isLoaded: PropTypes.bool,
    isFirstLoad: PropTypes.bool,
};


// Main ActionCards Component
const ActionCards = ({ cards, onCardClick = null, sortByUrgency = true, selectedKey = null, loading = false, loadedKeys = [], skeletonCount = 6, isFirstLoad = false, forceSkeletons = false }) => {
    // Sort cards by urgency if enabled
    const sortedCards = sortByUrgency
        ? [...cards].sort((a, b) => {
            const priorityDiff =
                PRIORITY_ORDER[a.urgencyState || URGENCY_STATES.NEUTRAL] -
                PRIORITY_ORDER[b.urgencyState || URGENCY_STATES.NEUTRAL];
            if (priorityDiff !== 0) return priorityDiff;
            // Secondary sort by count (descending) for same priority
            return b.count - a.count;
        })
        : cards;

    // Show skeleton cards while loading and no cards have data yet,
    // or when forceSkeletons is true (first session waiting for all data)
    // Skeletons key off loadedKeys, NOT cards.length.
    //
    // Every contextual widget (New's task buckets, Attended's Due Now row, Started's) builds
    // its cards array from CONSTANTS — key/label/icon are static and only the counts arrive
    // async — so cards.length is never 0 and this condition could never fire. Those boards
    // therefore showed no skeleton at all: they painted the full card row with '—' or 0 and
    // then swapped in real numbers. Only Booked appeared to work, because
    // BookedTableReportsWidget computes its own isLoading and renders ActionCardSkeleton
    // directly, bypassing this component.
    //
    // loadedKeys is the signal every consumer ALREADY provides correctly — empty while the
    // counts are unknown, filled once they land — so "fetching and nothing painted yet" is
    // exactly `loading && loadedKeys is empty`. A partially-loaded row (GeneralApp streams
    // its cards in) keeps rendering the cards it has rather than reverting to skeletons.
    const showSkeletons = forceSkeletons || (loading && (loadedKeys?.length || 0) === 0);
    const skeletonSlots = skeletonCount || 6;

    // Use framer-motion stagger container on first load, plain Box otherwise
    const WrapperComponent = isFirstLoad ? m.div : 'div';
    const wrapperProps = isFirstLoad
        ? { variants: containerVariants, initial: 'hidden', animate: 'visible' }
        : {};

    return (
        <Box component={WrapperComponent} {...wrapperProps} sx={cardWrapperStyle}>
            {showSkeletons ? (
                // Render skeleton placeholders while waiting for first data
                Array.from({ length: skeletonSlots }).map((_, index) => (
                    <ActionCardSkeleton key={`skeleton-${index}`} />
                ))
            ) : (
                sortedCards.map((card) => {
                    const cardEl = (
                        <ActionCard
                            key={card.key}
                            card={card}
                            onClick={onCardClick}
                            isSelected={selectedKey === card.key}
                            isLoaded={loadedKeys?.includes(card.key)}
                            isFirstLoad={isFirstLoad}
                        />
                    );
                    // Wrap each card in a motion div for stagger when first load
                    if (isFirstLoad) {
                        return (
                            <m.div key={card.key} variants={cardMotionVariants} style={{ flex: '1 1 0', display: 'flex' }}>
                                {cardEl}
                            </m.div>
                        );
                    }
                    return cardEl;
                })
            )}
        </Box>
    );
};

ActionCards.propTypes = {
    cards: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            count: PropTypes.number.isRequired,
            label: PropTypes.string.isRequired,
            icon: PropTypes.string,
            iconBgColor: PropTypes.string,
            urgencyState: PropTypes.oneOf(Object.values(URGENCY_STATES)),
            badges: PropTypes.array,
        })
    ).isRequired,
    onCardClick: PropTypes.func,
    sortByUrgency: PropTypes.bool,
    selectedKey: PropTypes.string,
    loading: PropTypes.bool,
    loadedKeys: PropTypes.array,
    skeletonCount: PropTypes.number,
    isFirstLoad: PropTypes.bool,
    forceSkeletons: PropTypes.bool,
};


export { ActionCards, ActionCard, ActionCardSkeleton, TimeBadge, URGENCY_STATES, URGENCY_COLORS, SELECTED_NEUTRAL_COLOR };
export default ActionCards;
